/**
 * 时间码设置菜单组件
 * 从 MainPage.tsx 提取，用于减少主文件大小
 */

import React from 'react';
import {
  Menu,
  Box,
  Typography,
  Button,
  Slider,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

interface TimecodeSettingsMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  
  // 时间码相关
  timecodePosition: string;
  setTimecodePosition: (position: string) => void;
  timecodeScale: number;
  setTimecodeScale: (scale: number) => void;
  showTimecode: boolean;
  setShowTimecode: (show: boolean) => void;
  setIsTimecodeDisabled: (disabled: boolean) => void;
  
  // 时钟相关
  showClock: boolean;
  setShowClock: (show: boolean) => void;
  showCurrentTime: boolean;
  setShowCurrentTime: (show: boolean) => void;
  showRemainingTime: boolean;
  setShowRemainingTime: (show: boolean) => void;
  timeFormat: '24h' | '12h';
  setTimeFormat: (format: '24h' | '12h') => void;
}

const TimecodeSettingsMenu: React.FC<TimecodeSettingsMenuProps> = ({
  anchorEl,
  open,
  onClose,
  timecodePosition,
  setTimecodePosition,
  timecodeScale,
  setTimecodeScale,
  showTimecode,
  setShowTimecode,
  setIsTimecodeDisabled,
  showClock,
  setShowClock,
  showCurrentTime,
  setShowCurrentTime,
  showRemainingTime,
  setShowRemainingTime,
  timeFormat,
  setTimeFormat
}) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      sx={{ 
        zIndex: 10000,
        '& .MuiBackdrop-root': {
          zIndex: 9998
        }
      }}
    >
      <Box sx={{ p: 2, minWidth: 300 }}>
        <Typography variant="h6" gutterBottom>
          時間碼設定
        </Typography>

        {/* 位置選擇 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            位置
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            {[
              { value: 'topLeft', label: '左上' },
              { value: 'topCenter', label: '中上' },
              { value: 'topRight', label: '右上' },
              { value: 'bottomLeft', label: '左下' },
              { value: 'bottomCenter', label: '中下' },
              { value: 'bottomRight', label: '右下' }
            ].map((position) => (
              <Button
                key={position.value}
                variant={timecodePosition === position.value ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setTimecodePosition(position.value);
                  setShowTimecode(true);
                  setIsTimecodeDisabled(false);
                }}
                sx={{ 
                  minWidth: 'auto',
                  fontSize: '0.75rem',
                  py: 0.5
                }}
              >
                {position.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* 字體大小調整 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            字體大小
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ minWidth: '60px' }}>
              小
            </Typography>
            <Slider
              value={timecodeScale}
              onChange={(_, value) => {
                setTimecodeScale(value as number);
                setShowTimecode(true);
                setIsTimecodeDisabled(false);
              }}
              min={0.3}
              max={1.2}
              step={0.1}
              sx={{ flex: 1 }}
            />
            <Typography variant="body2" sx={{ minWidth: '60px' }}>
              大
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            當前大小: {Math.round(timecodeScale * 100)}%
          </Typography>
        </Box>

        {/* 時鐘功能 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            時鐘功能
          </Typography>
          
          {/* 時鐘開關 */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Switch
              checked={showClock}
              onChange={(e) => setShowClock(e.target.checked)}
              size="small"
            />
            <Typography variant="body2" sx={{ ml: 1 }}>
              顯示時鐘
            </Typography>
          </Box>
          
          {showClock && (
            <>
              {/* 當前時間開關 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Switch
                  checked={showCurrentTime}
                  onChange={(e) => setShowCurrentTime(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  顯示當前時間
                </Typography>
              </Box>
              
              {/* 剩餘時間開關 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Switch
                  checked={showRemainingTime}
                  onChange={(e) => setShowRemainingTime(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  顯示剩餘時間
                </Typography>
              </Box>
              
              {/* 時間格式選擇 */}
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>時間格式</InputLabel>
                <Select
                  value={timeFormat}
                  onChange={(e) => setTimeFormat(e.target.value as '24h' | '12h')}
                  label="時間格式"
                >
                  <MenuItem value="24h">24小時制</MenuItem>
                  <MenuItem value="12h">12小時制</MenuItem>
                </Select>
              </FormControl>
              
              {/* 位置說明 */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                現在時間顯示在左下角，剩餘時間顯示在右下角
              </Typography>
            </>
          )}
        </Box>

        {/* 顯示/隱藏控制 */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setShowTimecode(true);
              setIsTimecodeDisabled(false);
              onClose();
            }}
            sx={{ flex: 1 }}
          >
            顯示時間碼
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShowTimecode(false);
              setIsTimecodeDisabled(false);
              onClose();
            }}
            sx={{ flex: 1 }}
          >
            隱藏時間碼
          </Button>
        </Box>
      </Box>
    </Menu>
  );
};

export default TimecodeSettingsMenu;

